import { formatAbn } from "utils/abn";
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import swal from "sweetalert";
import {
  LuChevronsUpDown,
  LuArrowLeft,
  LuCircleCheckBig,
  LuCreditCard,
  LuFileText,
  LuEye,
  LuEyeOff,
  LuMail,
  LuMenu,
  LuPencil,
  LuReceiptText,
  LuTrash2,
  LuUpload,
  LuX,
} from "react-icons/lu";

import AppHeader from "components/AppHeader";
import Skeleton from "components/Skeleton/Skeleton";
import ThemedSelect from "components/FormControls/ThemedSelect";
import { useMaterialUIController } from "context";
import { DashboardNavigation } from "layouts/businessUserDashboard";
import { appConstants } from "utils/appConstants";
import { clearAuthSession, setAuthSessionItem } from "utils/authSession";
import TeamSettings from "./TeamSettings";
import PlanBillingSettings from "./PlanBillingSettings";

import "./Settings.css";

const tabs = [
  ["profile", "Profile"],
  ["business", "Business"],
  ["reminders", "Reminders"],
  ["payment", "Payment details"],
  ["billing", "Plan & billing"],
  ["team", "Team"],
];

const initials = (value = "") =>
  String(value)
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase() || "IR";

const preloadImage = (src) =>
  new Promise((resolve) => {
    if (!src) {
      resolve();
      return;
    }
    const image = new Image();
    image.onload = resolve;
    image.onerror = resolve;
    image.src = src;
    if (image.complete) resolve();
  });

function SettingsCard({ children, className = "" }) {
  return <section className={`settings-card ${className}`}>{children}</section>;
}

function BusinessSettingsSkeleton() {
  return (
    <SettingsCard className="settings-business-card settings-business-skeleton">
      <div className="settings-business-skeleton-logo">
        <Skeleton width={150} height={13} radius={4} />
        <Skeleton width={310} height={11} radius={4} />
        <div>
          <Skeleton variant="rounded" width={48} height={48} radius={10} />
          <Skeleton variant="rounded" width={106} height={36} radius={8} />
          <Skeleton width={82} height={12} radius={4} />
        </div>
        <Skeleton width={330} height={11} radius={4} />
      </div>
      <div className="settings-business-skeleton-grid bordered">
        {[72, 68, 54].map((width, index) => (
          <div key={index}>
            <Skeleton width={`${width}%`} height={11} radius={4} />
            <Skeleton width="100%" height={40} radius={8} />
          </div>
        ))}
      </div>
      <Skeleton
        className="settings-business-skeleton-subheading"
        width={150}
        height={18}
        radius={4}
      />
      <div className="settings-business-skeleton-grid">
        {[64, 48, 55, 42, 38].map((width, index) => (
          <div className={index === 0 ? "wide" : ""} key={index}>
            <Skeleton width={`${width}%`} height={11} radius={4} />
            <Skeleton width="100%" height={40} radius={8} />
          </div>
        ))}
      </div>
      <div className="settings-business-skeleton-actions">
        <Skeleton variant="rounded" width={78} height={44} radius={8} />
        <Skeleton variant="rounded" width={120} height={44} radius={8} />
      </div>
    </SettingsCard>
  );
}

const profileFields = [
  "firstName",
  "lastName",
  "emailAddress",
  "phoneNumber",
  "userType",
  "companyName",
  "streetAddress",
  "city",
  "zipCode",
  "stateId",
  "countryId",
  "abnNumber",
  "industryType",
  "organizationType",
  "bankAccountNumber",
  "bankAccountName",
  "bankBsbCode",
];

const createProfileFormData = (profile, overrides = {}) => {
  const values = { ...profile, ...overrides };
  const formData = new FormData();
  profileFields.forEach((key) => {
    if (values[key] !== undefined && values[key] !== null) {
      formData.append(key, values[key]);
    }
  });
  formData.append("_id", values._id);
  formData.append("isbusinessUser", String(!values.isUserChild));
  formData.append("deletePPFlag", String(Boolean(values.deletePPFlag)));
  formData.append(
    "profilePicFileName",
    values.profilePictureFileName || values.profilePicFileName || ""
  );
  return formData;
};

const getProfileImage = (profile) => {
  const fileName =
    profile.profilePictureFileName || profile.profilePicFileName || "";
  return fileName
    ? `${appConstants.baseURL}user/viewFile?fileName=${encodeURIComponent(
        fileName
      )}`
    : "";
};

function ProfilePhotoDialog({ profile, onClose, onUpdated }) {
  const fileInput = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const name = `${profile.firstName || ""} ${profile.lastName || ""}`.trim();
  const savedPhotoUrl = getProfileImage(profile);

  useEffect(
    () => () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    },
    [previewUrl]
  );

  const selectPhoto = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!["image/jpeg", "image/png"].includes(file.type)) {
      setError("Choose a JPG or PNG image.");
      event.target.value = "";
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setError("Choose an image smaller than 2 MB.");
      event.target.value = "";
      return;
    }
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setError("");
    event.target.value = "";
  };

  const uploadPhoto = async () => {
    if (!selectedFile || uploading) return;
    setUploading(true);
    setError("");
    try {
      const formData = createProfileFormData(profile);
      formData.append("file", selectedFile);
      formData.append("fileName", selectedFile.name);
      const response = await axios.post(
        `${appConstants.baseURL}user/updateUserProfile`,
        formData
      );
      if (!response.data?.Result_Status) {
        throw new Error(response.data?.Result_Message || "Upload failed.");
      }
      const result = response.data?.Result_Output || {};
      onUpdated({
        ...profile,
        ...result,
        profilePictureFileName:
          result.profilePictureFileName ||
          result.profilePicFileName ||
          selectedFile.name,
      });
      onClose();
    } catch (uploadError) {
      setError(
        uploadError.response?.data?.Result_Message ||
          uploadError.message ||
          "Unable to update your photo."
      );
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="settings-photo-dialog-scrim">
      <section
        className="settings-photo-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="profile-photo-dialog-title"
      >
        <header>
          <h2 id="profile-photo-dialog-title">Add profile photo</h2>
          <button type="button" onClick={onClose} aria-label="Close dialog">
            <LuX aria-hidden="true" />
            <span>Close</span>
          </button>
        </header>
        <div className="settings-photo-dialog-body">
          <p>Upload a clear photo so your team can recognise you.</p>
          <div className="settings-photo-dialog-preview">
            {previewUrl || savedPhotoUrl ? (
              <img
                src={previewUrl || savedPhotoUrl}
                alt={
                  previewUrl ? "Selected profile preview" : "Current profile"
                }
              />
            ) : (
              <span className="settings-avatar">{initials(name)}</span>
            )}
            <strong>
              {previewUrl
                ? "Photo ready to upload"
                : savedPhotoUrl
                ? "Current profile photo"
                : "No profile photo yet"}
            </strong>
            <small>JPG or PNG · Max 2 MB · Recommended 400 × 400 px</small>
          </div>
          <input
            ref={fileInput}
            type="file"
            accept="image/jpeg,image/png"
            onChange={selectPhoto}
            hidden
          />
          <button
            type="button"
            className="settings-button primary settings-photo-choose"
            onClick={() => fileInput.current?.click()}
            disabled={uploading}
          >
            {previewUrl ? "Choose another photo" : "Choose a photo"}
          </button>
          <small className="settings-photo-dialog-note">
            Your photo is used to identify you in your workspace.
          </small>
          {error && <p className="settings-photo-dialog-error">{error}</p>}
        </div>
        <div className="settings-photo-dialog-actions">
          <button
            type="button"
            className="settings-button secondary"
            onClick={onClose}
            disabled={uploading}
          >
            Cancel
          </button>
          {selectedFile && (
            <button
              type="button"
              className="settings-button primary"
              onClick={uploadPhoto}
              disabled={uploading}
            >
              {uploading ? "Uploading..." : "Upload photo"}
            </button>
          )}
        </div>
      </section>
    </div>
  );
}

function BusinessLogoDialog({
  businessUserID,
  company,
  savedLogoUrl,
  onClose,
  onUpdated,
}) {
  const fileInput = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  useEffect(
    () => () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    },
    [previewUrl]
  );

  const selectLogo = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/svg+xml"].includes(file.type)) {
      setError("Choose a PNG, JPG or SVG image.");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setError("Choose an image smaller than 2 MB.");
      return;
    }
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setError("");
  };

  const uploadLogo = async () => {
    if (!selectedFile || !businessUserID || uploading) return;
    setUploading(true);
    setError("");
    try {
      const formData = new FormData();
      formData.append("businessUserID", businessUserID);
      formData.append("file", selectedFile);
      formData.append("fileName", selectedFile.name);
      const response = await axios.post(
        `${appConstants.baseURL}defaultSettings/updateDefaultSetting`,
        formData
      );
      if (!response.data?.Result_Status) {
        throw new Error(response.data?.Result_Message || "Upload failed.");
      }
      onUpdated(
        response.data?.Result_Output?.defaultLogoFileName || selectedFile.name
      );
      onClose();
    } catch (uploadError) {
      setError(
        uploadError.response?.data?.Result_Message ||
          uploadError.message ||
          "Unable to replace the business logo."
      );
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="settings-photo-dialog-scrim">
      <section
        className="settings-photo-dialog settings-business-logo-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="business-logo-dialog-title"
      >
        <header>
          <h2 id="business-logo-dialog-title">Replace business logo</h2>
          <button type="button" onClick={onClose} aria-label="Close dialog">
            <LuX aria-hidden="true" />
            <span>Close</span>
          </button>
        </header>
        <div className="settings-photo-dialog-body">
          <p>Choose the logo shown on your invoices and quotes.</p>
          <div className="settings-photo-dialog-preview">
            {previewUrl || savedLogoUrl ? (
              <img
                src={previewUrl || savedLogoUrl}
                alt={
                  previewUrl
                    ? "Selected business logo preview"
                    : "Current business logo"
                }
              />
            ) : (
              <span className="settings-business-logo-placeholder">
                {initials(company)}
              </span>
            )}
            <strong>
              {previewUrl
                ? "Logo ready to upload"
                : savedLogoUrl
                ? "Current business logo"
                : "Choose a new logo"}
            </strong>
            <small>
              PNG, JPG or SVG Â· Max 2 MB Â· Recommended 512 Ã— 512 px
            </small>
          </div>
          <input
            ref={fileInput}
            type="file"
            accept="image/jpeg,image/png,image/svg+xml"
            onChange={selectLogo}
            hidden
          />
          <button
            type="button"
            className="settings-button primary settings-photo-choose"
            onClick={() => fileInput.current?.click()}
            disabled={uploading}
          >
            <LuUpload aria-hidden="true" />
            {previewUrl ? "Choose another logo" : "Choose a logo"}
          </button>
          <small className="settings-photo-dialog-note">
            The existing logo remains unchanged until you upload this preview.
          </small>
          {error && <p className="settings-photo-dialog-error">{error}</p>}
        </div>
        <div className="settings-photo-dialog-actions">
          <button
            type="button"
            className="settings-button secondary"
            onClick={onClose}
            disabled={uploading}
          >
            Cancel
          </button>
          {selectedFile && (
            <button
              type="button"
              className="settings-button primary"
              onClick={uploadLogo}
              disabled={uploading}
            >
              {uploading ? "Uploading..." : "Upload logo"}
            </button>
          )}
        </div>
      </section>
    </div>
  );
}

function ProfileSettings({ user, onUserUpdated }) {
  const [profile, setProfile] = useState(user);
  const [values, setValues] = useState({ fullName: "", phoneNumber: "" });
  const [photoOpen, setPhotoOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const fullName =
    values.fullName ||
    profile.fullName ||
    `${profile.firstName || ""} ${profile.lastName || ""}`.trim() ||
    "Account owner";
  const profileImage = getProfileImage(profile);

  const commitProfile = (nextProfile) => {
    setProfile(nextProfile);
    onUserUpdated(nextProfile);
    setAuthSessionItem("LOGIN_DETAILS", JSON.stringify(nextProfile));
    window.dispatchEvent(new Event("user-profile-updated"));
  };

  useEffect(() => {
    let active = true;
    axios
      .post(`${appConstants.baseURL}user/fetchUserProfile`, { _id: user._id })
      .then((response) => {
        const result = response.data?.Result_Output?.[0];
        if (!active || !result) return;
        const next = { ...user, ...result };
        setProfile(next);
        setValues({
          fullName:
            next.fullName ||
            `${next.firstName || ""} ${next.lastName || ""}`.trim(),
          phoneNumber: next.phoneNumber || "",
        });
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [user]);

  useEffect(() => {
    setValues({
      fullName:
        profile.fullName ||
        `${profile.firstName || ""} ${profile.lastName || ""}`.trim(),
      phoneNumber: profile.phoneNumber || "",
    });
  }, [profile]);

  const saveDetails = async (event) => {
    event.preventDefault();
    if (!values.fullName.trim() || saving) return;
    const [firstName, ...lastName] = values.fullName.trim().split(/\s+/);
    const updates = {
      firstName,
      lastName: lastName.join(" "),
      phoneNumber: values.phoneNumber.trim(),
    };
    setSaving(true);
    setMessage("");
    try {
      const response = await axios.post(
        `${appConstants.baseURL}user/updateUserProfile`,
        createProfileFormData(profile, updates)
      );
      if (!response.data?.Result_Status) {
        throw new Error(response.data?.Result_Message || "Save failed.");
      }
      commitProfile({
        ...profile,
        ...updates,
        fullName: values.fullName.trim(),
      });
    } catch (saveError) {
      setMessage(
        saveError.response?.data?.Result_Message ||
          saveError.message ||
          "Unable to save your details."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <header className="settings-section-heading">
        <h2>Profile &amp; account</h2>
        <p>Manage your personal details and account security.</p>
      </header>
      <SettingsCard className="settings-photo-card">
        <h3>Profile photo</h3>
        <div className="settings-photo-row">
          {profileImage ? (
            <img src={profileImage} alt={`${fullName} profile`} />
          ) : (
            <span className="settings-avatar">{initials(fullName)}</span>
          )}
          <div>
            <p>
              Use a square JPG or PNG, up to 2 MB. Recommended size: 400 × 400
              px.
            </p>
            <div className="settings-photo-actions">
              <button
                className="settings-button secondary"
                type="button"
                onClick={() => setPhotoOpen(true)}
              >
                Change photo
              </button>
            </div>
          </div>
        </div>
      </SettingsCard>
      <form onSubmit={saveDetails}>
        <SettingsCard className="settings-personal-card">
          <h3>Personal details</h3>
          <p className="settings-card-copy">
            These details identify you as the account owner.
          </p>
          <div className="settings-profile-fields">
            <label>
              <span>Full name</span>
              <input
                value={values.fullName}
                onChange={(event) =>
                  setValues((current) => ({
                    ...current,
                    fullName: event.target.value,
                  }))
                }
                required
              />
            </label>
            <label>
              <span>Phone number (optional)</span>
              <input
                value={values.phoneNumber}
                onChange={(event) =>
                  setValues((current) => ({
                    ...current,
                    phoneNumber: event.target.value,
                  }))
                }
              />
            </label>
            <label className="wide">
              <span>Email address</span>
              <input
                value={profile.emailAddress || profile.email || ""}
                readOnly
              />
              <small>Contact support to change your sign-in email.</small>
            </label>
          </div>
          <div className="settings-role-row">
            <div>
              <span>Assigned role</span>
              <p>
                Your role controls access to workspace features and team
                permissions.
              </p>
            </div>
            <b>{profile.roleName || profile.userType || "Owner"}</b>
          </div>
          {message && <p className="settings-profile-message">{message}</p>}
          <div className="settings-card-actions">
            <button
              className="settings-button primary"
              type="submit"
              disabled={saving}
            >
              {saving ? "Saving..." : "Save changes"}
            </button>
          </div>
        </SettingsCard>
      </form>
      <SettingsCard className="settings-security-card">
        <div>
          <h3>Password &amp; security</h3>
          <span>Password</span>
          <p>For your security, passwords cannot be viewed here.</p>
        </div>
        <Link
          className="settings-button secondary"
          to="/settings/change-password"
        >
          Change password
        </Link>
      </SettingsCard>
      {photoOpen && (
        <ProfilePhotoDialog
          profile={profile}
          onClose={() => setPhotoOpen(false)}
          onUpdated={commitProfile}
        />
      )}
    </>
  );
}

function PasswordField({
  label,
  name,
  value,
  visible,
  helper,
  error,
  onChange,
  onToggle,
}) {
  const helperId = `${name}-helper`;
  return (
    <label className={`settings-password-field${error ? " error" : ""}`}>
      <span>
        {label} <b aria-hidden="true">*</b>
      </span>
      <span className="settings-password-input">
        <input
          type={visible ? "text" : "password"}
          name={name}
          value={value}
          placeholder="Enter password"
          autoComplete={
            name === "currentPassword" ? "current-password" : "new-password"
          }
          aria-invalid={Boolean(error)}
          aria-describedby={helperId}
          onChange={onChange}
        />
        <button
          type="button"
          aria-label={`${visible ? "Hide" : "Show"} ${label.toLowerCase()}`}
          onClick={onToggle}
        >
          {visible ? (
            <LuEyeOff aria-hidden="true" />
          ) : (
            <LuEye aria-hidden="true" />
          )}
        </button>
      </span>
      <small id={helperId}>{error || helper}</small>
    </label>
  );
}

function ChangePasswordSettings({ user, onCancel }) {
  const navigate = useNavigate();
  const [values, setValues] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [visible, setVisible] = useState({});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const updateValue = (event) => {
    const { name, value } = event.target;
    setValues((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: "", form: "" }));
  };

  const validate = () => {
    const next = {};
    if (!values.currentPassword.trim()) {
      next.currentPassword = "Enter your current password.";
    }
    if (!values.newPassword) {
      next.newPassword = "Enter a new password.";
    } else if (
      values.newPassword.length < 8 ||
      values.newPassword.length > 20
    ) {
      next.newPassword = "Use between 8 and 20 characters.";
    } else if (!/[A-Z]/.test(values.newPassword)) {
      next.newPassword = "Include at least one uppercase letter.";
    } else if (!/[!@#$%^&*(),.?":{}|<>]/.test(values.newPassword)) {
      next.newPassword = "Include at least one special character.";
    }
    if (!values.confirmPassword) {
      next.confirmPassword = "Enter the new password again.";
    } else if (values.confirmPassword !== values.newPassword) {
      next.confirmPassword = "Passwords do not match.";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const updatePassword = async (event) => {
    event.preventDefault();
    if (!validate() || submitting) return;

    setSubmitting(true);
    try {
      const profileResponse = await axios.post(
        `${appConstants.baseURL}user/fetchUserProfile`,
        { _id: user._id }
      );
      const profile = profileResponse.data?.Result_Output?.[0] || user;
      const formData = createProfileFormData({
        ...profile,
        _id: profile._id || user._id,
      });
      formData.append("currentPassword", values.currentPassword);
      formData.append("newPassword", values.newPassword);
      formData.append("confirmPassword", values.confirmPassword);

      const response = await axios.post(
        `${appConstants.baseURL}user/updateUserProfile`,
        formData
      );
      if (!response.data?.Result_Status) {
        setErrors({
          form:
            response.data?.Result_Message || "Unable to update your password.",
        });
        return;
      }

      await swal(
        "Password updated",
        "Sign in again with your new password.",
        "success"
      );
      clearAuthSession();
      navigate("/login", { replace: true });
    } catch (error) {
      setErrors({
        form:
          error.response?.data?.Result_Message ||
          "Unable to update your password. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="settings-password-flow">
      <header className="settings-section-heading">
        <h2>Change password</h2>
        <p>Update your password to help keep your account secure.</p>
      </header>
      <form
        className="settings-card settings-password-card"
        onSubmit={updatePassword}
      >
        <h3>Password details</h3>
        <PasswordField
          label="Current password"
          name="currentPassword"
          value={values.currentPassword}
          visible={visible.currentPassword}
          helper="Enter the password you currently use."
          error={errors.currentPassword}
          onChange={updateValue}
          onToggle={() =>
            setVisible((current) => ({
              ...current,
              currentPassword: !current.currentPassword,
            }))
          }
        />
        <PasswordField
          label="New password"
          name="newPassword"
          value={values.newPassword}
          visible={visible.newPassword}
          helper="Use at least 8 characters."
          error={errors.newPassword}
          onChange={updateValue}
          onToggle={() =>
            setVisible((current) => ({
              ...current,
              newPassword: !current.newPassword,
            }))
          }
        />
        <PasswordField
          label="Confirm new password"
          name="confirmPassword"
          value={values.confirmPassword}
          visible={visible.confirmPassword}
          helper="Enter the new password again to confirm."
          error={errors.confirmPassword}
          onChange={updateValue}
          onToggle={() =>
            setVisible((current) => ({
              ...current,
              confirmPassword: !current.confirmPassword,
            }))
          }
        />
        <p className="settings-password-security-copy">
          For security, you may be asked to sign in again after changing your
          password.
        </p>
        {errors.form && (
          <p className="settings-password-form-error" role="alert">
            {errors.form}
          </p>
        )}
        <div className="settings-password-actions">
          <button
            type="button"
            className="settings-button secondary"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="settings-button primary"
            disabled={submitting}
          >
            {submitting ? "Updating..." : "Update password"}
          </button>
        </div>
      </form>
    </div>
  );
}

function BusinessSettings({ user, onUserUpdated }) {
  const [controller] = useMaterialUIController();
  const company = user.companyName || user.businessName || "Your business";
  const storedUserId = sessionStorage
    .getItem("USER_ID")
    ?.replace(/["']/g, "")
    .trim();
  const profileUserId = user._id || storedUserId;
  const businessProfileId = user.businessUserID || user._id;
  const toBusinessForm = (profile) => ({
    companyName: profile.companyName || profile.businessName || "",
    organizationType: profile.organizationType || "Company",
    abnNumber: profile.abnNumber || "",
    streetAddress: profile.streetAddress || profile.companyStreetAddress || "",
    city: profile.city || profile.companyCity || "",
    stateName: profile.stateName || profile.state || "",
    zipCode: profile.zipCode || profile.companyZipCode || "",
  });
  const [form, setForm] = useState({
    companyName: company,
    organizationType: user.organizationType || "Company",
    abnNumber: user.abnNumber || "",
    streetAddress: user.streetAddress || "",
    city: user.city || "",
    stateName: user.stateName || user.state || "",
    zipCode: user.zipCode || "",
  });
  const [savedBusinessProfile, setSavedBusinessProfile] = useState(user);
  const [loadingBusiness, setLoadingBusiness] = useState(true);
  const [loadingLogo, setLoadingLogo] = useState(true);
  const [savingBusiness, setSavingBusiness] = useState(false);
  const [removingLogo, setRemovingLogo] = useState(false);
  const [logoRemoved, setLogoRemoved] = useState(false);
  const [logoOpen, setLogoOpen] = useState(false);
  const [logoFileName, setLogoFileName] = useState("");
  const logoUrl = logoFileName
    ? `${appConstants.baseURL}employee/viewFile?fileName=${encodeURIComponent(
        logoFileName
      )}`
    : "";

  useEffect(() => {
    if (!profileUserId) {
      setLoadingBusiness(false);
      return undefined;
    }
    let active = true;
    setLoadingBusiness(true);
    axios
      .post(`${appConstants.baseURL}user/fetchUserProfile`, {
        _id: profileUserId,
      })
      .then((response) => {
        const output = response.data?.Result_Output;
        const result = Array.isArray(output) ? output[0] : output;
        if (!active || !result) return;
        const next = { ...user, ...result };
        setSavedBusinessProfile(next);
        setForm(toBusinessForm(next));
      })
      .catch(() => {})
      .finally(() => {
        if (active) setLoadingBusiness(false);
      });
    return () => {
      active = false;
    };
  }, [profileUserId, user]);

  useEffect(() => {
    if (!businessProfileId) {
      setLoadingLogo(false);
      return undefined;
    }
    let active = true;
    setLoadingLogo(true);
    axios
      .post(`${appConstants.baseURL}defaultSettings/fetchDefaultSetting`, {
        businessUserID: businessProfileId,
      })
      .then(async (response) => {
        if (!active) return;
        const fileName =
          response.data?.Result_Output?.defaultLogoFileName || "";
        await preloadImage(
          fileName
            ? `${
                appConstants.baseURL
              }employee/viewFile?fileName=${encodeURIComponent(fileName)}`
            : ""
        );
        if (!active) return;
        setLogoFileName(fileName);
        setLogoRemoved(!fileName);
      })
      .catch(() => {})
      .finally(() => {
        if (active) setLoadingLogo(false);
      });
    return () => {
      active = false;
    };
  }, [businessProfileId]);

  const removeBusinessLogo = async () => {
    const businessUserID = businessProfileId;
    if (!businessUserID || removingLogo) return;

    const confirmed = await swal({
      title: "Remove business logo?",
      text: "Your initials will be shown until you upload another logo.",
      icon: "warning",
      buttons: ["Cancel", "Remove"],
      dangerMode: true,
    });
    if (!confirmed) return;

    setRemovingLogo(true);
    try {
      const response = await axios.post(
        `${appConstants.baseURL}defaultSettings/deleteBusinessUserLogo`,
        { businessUserID }
      );
      if (response.data?.Result_Status) {
        setLogoRemoved(true);
        setLogoFileName("");
        window.dispatchEvent(
          new CustomEvent("inr:business-logo-changed", {
            detail: { businessUserID, defaultLogoFileName: "" },
          })
        );
        await swal("Logo removed", response.data.Result_Message, "success");
      } else {
        await swal(
          "Unable to remove logo",
          response.data?.Result_Message || "Please try again.",
          "error"
        );
      }
    } catch (error) {
      await swal(
        "Unable to remove logo",
        error.response?.data?.Result_Message || "Please try again.",
        "error"
      );
    } finally {
      setRemovingLogo(false);
    }
  };
  const updateBusinessField = (event) =>
    setForm((current) => ({
      ...current,
      [event.target.name]: event.target.value,
    }));
  const saveBusiness = async (event) => {
    event.preventDefault();
    if (savingBusiness) return;
    setSavingBusiness(true);
    try {
      const response = await axios.post(
        `${appConstants.baseURL}user/updateUserProfile`,
        createProfileFormData(savedBusinessProfile, form)
      );
      if (!response.data?.Result_Status) {
        throw new Error(
          response.data?.Result_Message || "Unable to save business details."
        );
      }
      const next = { ...savedBusinessProfile, ...form };
      setSavedBusinessProfile(next);
      setAuthSessionItem("LOGIN_DETAILS", JSON.stringify(next));
      onUserUpdated(next);
    } catch (error) {
      await swal(
        "Unable to save business details",
        error.response?.data?.Result_Message ||
          error.message ||
          "Please check the details and try again.",
        "error"
      );
    } finally {
      setSavingBusiness(false);
    }
  };

  return (
    <>
      <header className="settings-section-heading">
        <h2>Business profile</h2>
        <p>
          Manage your business details. These details will be used on your
          invoices and quotes.
        </p>
      </header>
      {(loadingBusiness || loadingLogo) && <BusinessSettingsSkeleton />}
      {!loadingBusiness && !loadingLogo && (
        <form onSubmit={saveBusiness}>
          <SettingsCard className="settings-business-card">
            <div className="settings-business-logo">
              <div className="settings-business-logo-copy">
                <span>
                  Business logo <em>Optional</em>
                </span>
                <p>This will be displayed on your invoices and quotes.</p>
              </div>
              <div className="settings-business-logo-controls">
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt={`${form.companyName || company} logo`}
                  />
                ) : (
                  <i>{initials(form.companyName || company)}</i>
                )}
                <button
                  type="button"
                  className="settings-button secondary settings-logo-action"
                  onClick={() => setLogoOpen(true)}
                >
                  <LuUpload aria-hidden="true" />
                  Replace
                </button>
                {!logoRemoved && (
                  <button
                    type="button"
                    className="settings-button settings-logo-remove"
                    onClick={removeBusinessLogo}
                    disabled={removingLogo || !businessProfileId}
                  >
                    <LuTrash2 aria-hidden="true" />
                    {removingLogo ? "Removing" : "Remove"}
                  </button>
                )}
              </div>
              <p className="settings-business-logo-recommendation">
                Recommended size: 512 × 512px. PNG, JPG or SVG (max 2MB).
              </p>
            </div>
            <div className="settings-detail-grid bordered">
              <div>
                <span>Business / legal name</span>
                <input
                  name="companyName"
                  value={form.companyName}
                  onChange={updateBusinessField}
                  required
                  disabled={loadingBusiness}
                />
              </div>
              <div>
                <span>Organisation type</span>
                <ThemedSelect
                  id="settings-organisation-type"
                  className="settings-business-organisation-select"
                  aria-label="Organisation type"
                  dark={controller.darkMode}
                  value={form.organizationType}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      organizationType: event.target.value,
                    }))
                  }
                  disabled={loadingBusiness}
                >
                  <option value="Company">Company</option>
                  <option value="Individual">Individual</option>
                  <option value="Partnership">Partnership</option>
                  <option value="Trust">Trust</option>
                </ThemedSelect>
              </div>
              <div className="settings-abn-field">
                <span>Australian Business Number (ABN)</span>
                <input
                  name="abnNumber"
                  value={formatAbn(form.abnNumber)}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      abnNumber: event.target.value
                        .replace(/\D/g, "")
                        .slice(0, 11),
                    })
                  }
                  disabled={loadingBusiness}
                />
              </div>
            </div>
            <h3 className="settings-subheading">Business address</h3>
            <div className="settings-detail-grid">
              <div className="wide">
                <span>Street address</span>
                <input
                  name="streetAddress"
                  value={form.streetAddress}
                  onChange={updateBusinessField}
                  disabled={loadingBusiness}
                />
              </div>
              <div>
                <span>Suburb / city</span>
                <input
                  name="city"
                  value={form.city}
                  onChange={updateBusinessField}
                  disabled={loadingBusiness}
                />
              </div>
              <div>
                <span>State / Territory</span>
                <input
                  name="stateName"
                  value={form.stateName}
                  onChange={updateBusinessField}
                  disabled={loadingBusiness}
                />
              </div>
              <div>
                <span>Postcode</span>
                <input
                  name="zipCode"
                  value={form.zipCode}
                  onChange={updateBusinessField}
                  disabled={loadingBusiness}
                />
              </div>
              <div>
                <span>Country</span>
                <input
                  value={
                    savedBusinessProfile.countryName ||
                    savedBusinessProfile.country ||
                    "Australia"
                  }
                  readOnly
                />
              </div>
            </div>
            <div className="settings-card-actions">
              <button
                type="button"
                className="settings-button secondary"
                onClick={() => setForm(toBusinessForm(savedBusinessProfile))}
                disabled={loadingBusiness || savingBusiness}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="settings-button primary"
                disabled={loadingBusiness || savingBusiness}
              >
                {loadingBusiness
                  ? "Loading..."
                  : savingBusiness
                  ? "Saving..."
                  : "Save changes"}
              </button>
            </div>
          </SettingsCard>
        </form>
      )}
      {logoOpen && (
        <BusinessLogoDialog
          businessUserID={businessProfileId}
          company={form.companyName || company}
          savedLogoUrl={logoUrl}
          onClose={() => setLogoOpen(false)}
          onUpdated={(fileName) => {
            setLogoFileName(fileName);
            setLogoRemoved(false);
            window.dispatchEvent(
              new CustomEvent("inr:business-logo-changed", {
                detail: {
                  businessUserID: businessProfileId,
                  defaultLogoFileName: fileName,
                },
              })
            );
          }}
        />
      )}
    </>
  );
}

const reminderDefaults = [
  [
    "Friendly reminder",
    3,
    "A friendly reminder that this invoice is now overdue.",
  ],
  ["Payment follow up", 7, "Just following up on this overdue invoice."],
  ["Final reminder", 14, "This is a final reminder before further action."],
  ["Letter of demand", 21, "Formal notice before the debt case opens."],
];

function ReminderPreview({ reminder, onClose }) {
  return (
    <div className="settings-sheet-scrim">
      <section
        className="settings-sheet reminder-preview"
        role="dialog"
        aria-modal="true"
      >
        <i className="settings-sheet-handle" />
        <header>
          <div>
            <h3>Preview reminder</h3>
            <p>This is an example of the email your customer will receive.</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close">
            <LuX />
          </button>
        </header>
        <div className="reminder-preview-summary">
          <LuMail />
          <div>
            <strong>{reminder[0]}</strong>
            <span>{reminder[1]} days after due date</span>
          </div>
        </div>
        <div className="reminder-preview-meta">
          <span>To</span>
          <b>customer@mapleelectrical.com.au</b>
          <span>Subject</span>
          <b>{reminder[0]}: Invoice INV-001 is overdue</b>
        </div>
        <p>Saved changes only · Audit events cannot be edited.</p>
        <article>
          <h4>Maple Electrical</h4>
          <p>Hi Maple Electrical,</p>
          <p>
            A friendly reminder that invoice INV-001 for $1,200.00 was due on 12
            Oct 2026 and is now overdue.
          </p>
          <p>If you’ve already made a payment, please ignore this message.</p>
          <p>
            Thanks,
            <br />
            Maple Electrical
          </p>
          <aside>
            ⓘ &nbsp; Example content only. Customer and invoice information is
            populated dynamically.
          </aside>
        </article>
      </section>
    </div>
  );
}

function RemindersSettings() {
  const [enabled, setEnabled] = useState(false);
  const [preview, setPreview] = useState(null);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({
    days: 3,
    subject: "Friendly reminder",
    message:
      "Hi {{customer_name}},\n\nA friendly reminder that invoice {{invoice_number}} for {{invoice_amount}} was due on {{due_date}} and is now overdue.\n\nIf you’ve already made a payment, please ignore this message.",
  });

  if (editing !== null) {
    return (
      <div className="reminder-edit-page">
        <button
          type="button"
          className="settings-inline-back"
          onClick={() => setEditing(null)}
        >
          <LuArrowLeft /> Back to Reminders
        </button>
        <header className="settings-section-heading">
          <h2>Edit reminder</h2>
        </header>
        <SettingsCard className="reminder-edit-card">
          <h3>Timing</h3>
          <label>Send this reminder</label>
          <div className="reminder-timing">
            <input
              value={draft.days}
              onChange={(e) => setDraft({ ...draft, days: e.target.value })}
            />
            <select defaultValue="day">
              <option>day</option>
              <option>days</option>
            </select>
            <span>after due date</span>
          </div>
          <label>Subject</label>
          <input
            value={draft.subject}
            onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
          />
          <div className="reminder-toolbar">
            <select>
              <option>Normal</option>
            </select>
            <b>B</b>
            <i>I</i>
            <u>U</u>
            <span>🔗</span>
            <span>☷</span>
          </div>
          <label>Message</label>
          <textarea
            value={draft.message}
            onChange={(e) => setDraft({ ...draft, message: e.target.value })}
          />
          <aside>
            ⓘ &nbsp; This is a preview of how the email will look. Customer
            details and invoice information will be dynamically added.
          </aside>
          <footer>
            <button
              type="button"
              className="settings-button secondary"
              onClick={() => setEditing(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="settings-button primary"
              onClick={() => setEditing(null)}
            >
              Save changes
            </button>
          </footer>
        </SettingsCard>
        <div className="reminder-email-preview">
          <span>✉ &nbsp; To: customer@business.com</span>
          <article>
            <p>Subject: {draft.subject}</p>
            <p>Hi Acme Pty Ltd,</p>
            <p>
              A friendly reminder that invoice INV-001 for $1,200.00 was due on
              12 Jan 2026 and is now overdue.
            </p>
            <p>If you’ve already made a payment, please ignore this message.</p>
            <button type="button" className="settings-button secondary">
              View invoice
            </button>
            <p>
              Thanks,
              <br />
              John Doe
              <br />
              Acme Pty Ltd
            </p>
          </article>
        </div>
      </div>
    );
  }

  return (
    <>
      <header className="settings-section-heading settings-reminder-heading">
        <div>
          <h2>Invoice reminders</h2>
          <p>
            Send automated reminders to customers for overdue invoices. You can
            customise the timing and message for each reminder.
          </p>
        </div>
        <label className="settings-switch">
          <input
            type="checkbox"
            checked={enabled}
            onChange={() => setEnabled(!enabled)}
          />
          <span />
          {enabled ? "On" : "Off"}
        </label>
      </header>
      {!enabled ? (
        <SettingsCard className="settings-empty">
          <span>
            <LuMail />
          </span>
          <h3>Reminders are turned off</h3>
          <p>
            Turn on invoice reminders to automatically follow up with customers
            about overdue invoices.
          </p>
          <button
            type="button"
            className="settings-button primary"
            onClick={() => setEnabled(true)}
          >
            Turn on reminders
          </button>
        </SettingsCard>
      ) : (
        <SettingsCard className="reminder-list">
          {reminderDefaults.map((item, index) => (
            <div className="reminder-row" key={item[0]}>
              <i>
                <LuMail />
              </i>
              <div>
                <strong>{item[0]}</strong>
                <span>{item[1]} days after due date</span>
              </div>
              <button
                type="button"
                aria-label={`Preview ${item[0]}`}
                onClick={() => setPreview(item)}
              >
                <LuEye />
              </button>
              <button
                type="button"
                aria-label={`Edit ${item[0]}`}
                onClick={() => {
                  setEditing(index);
                  setDraft((current) => ({
                    ...current,
                    days: item[1],
                    subject: item[0],
                  }));
                }}
              >
                <LuPencil />
              </button>
            </div>
          ))}
        </SettingsCard>
      )}
      {preview && (
        <ReminderPreview reminder={preview} onClose={() => setPreview(null)} />
      )}
    </>
  );
}

function PaymentSettings({ user, onUserUpdated }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const returnTo = params.get("returnTo");
  const [editing, setEditing] = useState(params.get("edit") === "bank");
  const [saved, setSaved] = useState(Boolean(user.bankAccountNumber));
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    name: user.bankAccountName || user.companyName || "",
    bsb: user.bankBsbCode || "",
    number: user.bankAccountNumber || "",
    confirmNumber: user.bankAccountNumber || "",
  });

  const updateField = (key, value) => {
    setForm((current) => ({
      ...current,
      [key]: key === "name" ? value : value.replace(/\D/g, ""),
    }));
    setErrors((current) => ({ ...current, [key]: "", form: "" }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Enter the account name.";
    if (!/^\d{6}$/.test(form.bsb)) next.bsb = "Enter a valid 6-digit BSB.";
    if (!/^\d{6,10}$/.test(form.number)) {
      next.number = "Enter a valid account number.";
    }
    if (!form.confirmNumber) {
      next.confirmNumber = "Confirm the account number.";
    } else if (form.confirmNumber !== form.number) {
      next.confirmNumber = "Account numbers do not match.";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const save = async (event) => {
    event.preventDefault();
    if (!validate() || saving) return;
    const updates = {
      bankAccountName: form.name.trim(),
      bankBsbCode: form.bsb,
      bankAccountNumber: form.number,
    };
    setSaving(true);
    try {
      const response = await axios.post(
        `${appConstants.baseURL}user/updateUserProfile`,
        createProfileFormData(user, updates)
      );
      if (!response.data?.Result_Status) throw new Error();
      const next = { ...user, ...updates };
      setAuthSessionItem("LOGIN_DETAILS", JSON.stringify(next));
      onUserUpdated(next);
      setSaved(true);
      setEditing(false);
      if (returnTo?.startsWith("/invoices")) navigate(returnTo);
    } catch (_) {
      setErrors({
        form: "Unable to save your bank details. Please try again.",
      });
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <>
        <header className="settings-section-heading settings-payment-edit-heading">
          <h2>{saved ? "Edit bank details" : "Add bank details"}</h2>
          <p>
            {saved
              ? "Update your bank details. Enter the new account number below and confirm it to save your changes."
              : "Add your bank account details to receive payments from your customers."}
          </p>
        </header>
        <form onSubmit={save}>
          <SettingsCard className="payment-edit-card">
            {[
              [
                "name",
                "Account name",
                "Use the name shown on your bank account.",
                100,
              ],
              ["bsb", "BSB", "Enter the 6-digit BSB.", 6],
              [
                "number",
                "Account number",
                "Enter the account number you want to use.",
                10,
              ],
              [
                "confirmNumber",
                "Confirm account number",
                "Re-enter the account number to confirm.",
                10,
              ],
            ].map(([key, label, helper, maxLength]) => (
              <label key={key} className={errors[key] ? "error" : ""}>
                <span className="payment-required-label">
                  {label} <b aria-hidden="true">*</b>
                </span>
                <input
                  inputMode={key === "name" ? undefined : "numeric"}
                  maxLength={maxLength}
                  value={form[key]}
                  onChange={(event) => updateField(key, event.target.value)}
                  aria-invalid={Boolean(errors[key])}
                />
                <small>{errors[key] || helper}</small>
              </label>
            ))}
            {errors.form && <p className="payment-form-error">{errors.form}</p>}
            <footer>
              <button
                type="button"
                className="settings-button secondary"
                onClick={() => setEditing(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="settings-button primary"
                disabled={saving}
              >
                {saving
                  ? "Saving..."
                  : saved
                  ? "Save changes"
                  : "Add bank details"}
              </button>
            </footer>
          </SettingsCard>
        </form>
      </>
    );
  }

  return (
    <>
      <header className="settings-section-heading settings-heading-action settings-payment-heading">
        <div>
          <h2>Payment details</h2>
          <p>
            {saved
              ? "Your bank account details are used to receive payments from invoices and quotes."
              : "Add your bank account details to receive payments from your customers."}
          </p>
        </div>
        {saved && (
          <button
            type="button"
            className="settings-button secondary"
            onClick={() => setEditing(true)}
          >
            <LuPencil /> Edit details
          </button>
        )}
      </header>
      {saved ? (
        <SettingsCard className="payment-details-card">
          <div className="payment-success">
            <LuCircleCheckBig />
            <div>
              <strong>Bank details added</strong>
              <p>
                You’re all set to receive payments from invoices and quotes.
              </p>
            </div>
          </div>
          <div className="payment-values">
            <span>Account name</span>
            <strong>{form.name}</strong>
            <span>BSB</span>
            <strong>{form.bsb}</strong>
            <span>Account number</span>
            <strong>•••• {form.number.slice(-4)}</strong>
          </div>
          <p>
            Your bank details are securely stored and only used to receive
            payments.
          </p>
        </SettingsCard>
      ) : (
        <SettingsCard className="settings-empty payment-empty-card">
          <span>
            <LuMail />
          </span>
          <h3>No bank details added</h3>
          <p>
            Add bank account details to receive payments from invoices and
            quotes. You can manage these anytime in Settings.
          </p>
          <button
            type="button"
            className="settings-button primary"
            onClick={() => setEditing(true)}
          >
            Add bank details
          </button>
        </SettingsCard>
      )}
    </>
  );
}

// Retained while legacy subscription routes still use the previous card vocabulary.
// eslint-disable-next-line no-unused-vars
function BillingSettings({ user }) {
  const plan = user.planName || user.subscriptionPlan || "Silver";
  const invoices = user.invoiceCount || user.usedInvoices || 0;
  const invoiceLimit = user.invoiceLimit || 10;
  const quotes = user.quotationCount || user.usedQuotations || 0;
  const quoteLimit = user.quotationLimit || 100;
  return (
    <>
      <header className="settings-section-heading">
        <h2>Plan &amp; billing</h2>
        <p>
          Manage your subscription, track usage and credits, and view billing
          details and invoice history.
        </p>
      </header>
      <SettingsCard className="settings-plan">
        <small>YOUR CURRENT PLAN</small>
        <h3>{plan}</h3>
        <strong>
          {user.planPrice
            ? `$${user.planPrice} AUD / month`
            : "Current subscription"}
        </strong>
        <p>
          Invoices {invoices} / {invoiceLimit} · Quotations {quotes} /{" "}
          {quoteLimit}
        </p>
      </SettingsCard>
      <div className="settings-stat-grid">
        <SettingsCard>
          <LuReceiptText />
          <span>Invoices</span>
          <h3>
            {invoices} / {invoiceLimit}
          </h3>
        </SettingsCard>
        <SettingsCard>
          <LuFileText />
          <span>Quotations</span>
          <h3>
            {quotes} / {quoteLimit}
          </h3>
        </SettingsCard>
        <SettingsCard>
          <LuCreditCard />
          <span>Credits</span>
          <h3>{user.credits || 0}</h3>
        </SettingsCard>
      </div>
      <div className="settings-link-grid">
        <SettingsCard>
          <h3>Invoice history</h3>
          <p>View and download past subscription invoices.</p>
          <Link to="/subscriptions">View invoice history →</Link>
        </SettingsCard>
        <SettingsCard>
          <h3>Billing details</h3>
          <p>Manage billing contact and payment method.</p>
          <Link to="/subscriptions">Manage billing details →</Link>
        </SettingsCard>
        <SettingsCard>
          <h3>Manage plan</h3>
          <p>Review plan features and available options.</p>
          <Link to="/subscriptions">Review plans →</Link>
        </SettingsCard>
      </div>
    </>
  );
}

export default function Settings() {
  const [controller] = useMaterialUIController();
  const [navOpen, setNavOpen] = useState(false);
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(() =>
    JSON.parse(sessionStorage.getItem("LOGIN_DETAILS") || "{}")
  );
  const requestedTab = params.get("tab");
  const legacyBusinessRoute = window.location.pathname === "/defaultSettings";
  const legacyTeamRoute = /^\/my(users|roles)/.test(window.location.pathname);
  const passwordRoute =
    window.location.pathname === "/settings/change-password";
  const active = tabs.some(([key]) => key === requestedTab)
    ? requestedTab
    : passwordRoute
    ? "profile"
    : legacyBusinessRoute
    ? "business"
    : legacyTeamRoute
    ? "team"
    : "profile";
  const mobileTabsStartWithBilling = ["payment", "billing", "team"].includes(
    active
  );
  const organisation =
    user.companyName || user.businessName || "Invoice & Recover";
  const profileFile = user.profilePictureFileName || user.profilePicFileName;
  const profileImage = profileFile
    ? `${appConstants.baseURL}user/viewFile?fileName=${encodeURIComponent(
        profileFile
      )}`
    : "";
  const role = user.roleName || user.userType || "Owner";
  const selectTab = (key) => {
    if (passwordRoute) {
      navigate(`/settings?tab=${key}`);
      return;
    }
    setParams({ tab: key });
  };
  return (
    <div
      className={`settings-page business-dashboard${
        controller.darkMode ? " dark" : ""
      }`}
      data-theme={controller.darkMode ? "dark" : "light"}
    >
      <DashboardNavigation
        open={navOpen}
        onClose={() => setNavOpen(false)}
        activePath="/settings"
        organisation={organisation}
        role={role}
        planName={user.planName || "SILVER"}
      />
      <div className="dashboard-main settings-main">
        <AppHeader
          onOpenNav={() => setNavOpen(true)}
          userName={user.fullName || organisation}
          profileImage={profileImage}
        />
        <main className="settings-content">
          <div className="settings-responsive-context">
            <div className="settings-product-identity">
              <button
                type="button"
                onClick={() => setNavOpen(true)}
                aria-label="Open navigation"
              >
                <LuMenu />
              </button>
              <span>invoice &amp; recover</span>
            </div>
            <div className="settings-organisation-selector">
              <i>{initials(organisation)}</i>
              <span>
                <strong>{organisation}</strong>
                <small>{role}</small>
              </span>
              <LuChevronsUpDown />
            </div>
          </div>
          <h1>Settings</h1>
          <nav
            className={`settings-tabs${
              mobileTabsStartWithBilling ? " mobile-billing-first" : ""
            }`}
            aria-label="Settings sections"
          >
            {tabs.map(([key, label]) => (
              <button
                key={key}
                type="button"
                className={active === key ? "active" : ""}
                onClick={() => selectTab(key)}
              >
                {label}
              </button>
            ))}
          </nav>
          <div className="settings-panel">
            {passwordRoute ? (
              <ChangePasswordSettings
                user={user}
                onCancel={() => navigate("/settings?tab=profile")}
              />
            ) : active === "profile" ? (
              <ProfileSettings user={user} onUserUpdated={setUser} />
            ) : null}
            {!passwordRoute && active === "business" && (
              <BusinessSettings user={user} onUserUpdated={setUser} />
            )}
            {active === "reminders" && <RemindersSettings />}
            {active === "payment" && (
              <PaymentSettings user={user} onUserUpdated={setUser} />
            )}
            {active === "billing" && (
              <PlanBillingSettings
                user={user}
                initialView={
                  params.get("view") === "credits" ? "credits" : "overview"
                }
              />
            )}
            {active === "team" && <TeamSettings />}
          </div>
        </main>
      </div>
    </div>
  );
}
